export type PrefetchPoint = {
  readonly x: number
  readonly z: number
}

export type PrefetchDirection = {
  readonly x: number
  readonly z: number
}

/**
 * Retains blocked intent until the slower queue cadence consumes it. A large
 * simulation-pose discontinuity also counts as intent because recovery can
 * restore presentation before WorldStreamer.update observes that pose.
 */
export function retainMotionPrefetchPoint(
  existing: PrefetchPoint | undefined,
  lastObservedPosition: PrefetchPoint | undefined,
  start: PrefetchPoint,
  end: PrefetchPoint,
  collided: boolean,
  discontinuityMeters: number,
): PrefetchPoint | undefined {
  const finiteStart = Number.isFinite(start.x) && Number.isFinite(start.z)
  const finiteEnd = Number.isFinite(end.x) && Number.isFinite(end.z)
  const threshold = Number.isFinite(discontinuityMeters) ? Math.max(0, discontinuityMeters) : Number.POSITIVE_INFINITY
  // Recovery can itself collide at the restored pose. Preserve the outward
  // intent when the simulation has discontinuously jumped away from the last
  // observed pose; normal contiguous blocked attempts may still replace it.
  if (
    existing !== undefined
    && finiteStart
    && lastObservedPosition !== undefined
    && Number.isFinite(lastObservedPosition.x)
    && Number.isFinite(lastObservedPosition.z)
    && Math.hypot(start.x - lastObservedPosition.x, start.z - lastObservedPosition.z) > threshold
  ) return existing
  // A zero-length collision is produced while recovery settles at its safe
  // pose. It is not newer travel intent and must not replace the outward pose.
  if (collided && finiteEnd && finiteStart && Math.hypot(end.x - start.x, end.z - start.z) > 1e-6) return end
  if (
    finiteStart
    && lastObservedPosition !== undefined
    && Number.isFinite(lastObservedPosition.x)
    && Number.isFinite(lastObservedPosition.z)
    && Math.hypot(start.x - lastObservedPosition.x, start.z - lastObservedPosition.z) > threshold
  ) return start
  return existing
}

/**
 * Captures a simulation pose that presentation may recover from before the
 * regular queue tick. An already captured pose wins until it is consumed, so
 * the reciprocal recovery jump cannot replace the original outward intent.
 */
export function retainDiscontinuousPrefetchPoint(
  existing: PrefetchPoint | undefined,
  lastObservedPosition: PrefetchPoint | undefined,
  currentPosition: PrefetchPoint,
  discontinuityMeters: number,
): PrefetchPoint | undefined {
  if (existing !== undefined) return existing
  return retainMotionPrefetchPoint(
    undefined,
    lastObservedPosition,
    currentPosition,
    currentPosition,
    false,
    discontinuityMeters,
  )
}

/** Retains the heading observed at the intended pose across presentation recovery. */
export function retainPrefetchDirection(
  existing: PrefetchDirection | undefined,
  intendedPosition: PrefetchPoint,
  observedPosition: PrefetchPoint,
  observedDirection: PrefetchDirection,
  captureRadiusMeters = 1,
): PrefetchDirection | undefined {
  if (
    ![intendedPosition.x, intendedPosition.z, observedPosition.x, observedPosition.z].every(Number.isFinite)
    || !Number.isFinite(observedDirection.x)
    || !Number.isFinite(observedDirection.z)
  ) return existing
  const captureRadius = Number.isFinite(captureRadiusMeters) ? Math.max(0, captureRadiusMeters) : 0
  if (Math.hypot(observedPosition.x - intendedPosition.x, observedPosition.z - intendedPosition.z) > captureRadius) return existing
  if (Math.hypot(observedDirection.x, observedDirection.z) <= 0.01) return existing
  return observedDirection
}

/**
 * Produces deterministic world-space probes for one single-flight prefetch.
 * A rejected simulation destination is authoritative intent and therefore
 * ranks before the clamped vehicle position. The directional lookahead comes
 * next, followed by stable cardinal/diagonal fallbacks.
 */
export function rankedPrefetchPoints(
  position: PrefetchPoint,
  direction: PrefetchDirection,
  lookaheadMeters: number,
  intendedDestination?: PrefetchPoint,
): PrefetchPoint[] {
  const lookahead = Math.max(0, Number.isFinite(lookaheadMeters) ? lookaheadMeters : 0)
  const directionLength = Math.hypot(direction.x, direction.z)
  const targets: PrefetchPoint[] = []
  if (intendedDestination !== undefined && Number.isFinite(intendedDestination.x) && Number.isFinite(intendedDestination.z)) {
    targets.push(intendedDestination)
  }
  if (directionLength > 0.01) {
    targets.push({
      x: position.x + direction.x / directionLength * lookahead,
      z: position.z + direction.z / directionLength * lookahead,
    })
  }
  targets.push(
    { x: position.x + lookahead, z: position.z },
    { x: position.x - lookahead, z: position.z },
    { x: position.x, z: position.z + lookahead },
    { x: position.x, z: position.z - lookahead },
    { x: position.x + lookahead, z: position.z + lookahead },
    { x: position.x + lookahead, z: position.z - lookahead },
    { x: position.x - lookahead, z: position.z + lookahead },
    { x: position.x - lookahead, z: position.z - lookahead },
  )
  return targets
}

/** Selects at most one missing key while retaining the ranked probe order. */
export function selectSinglePrefetchCandidate<T>(
  points: readonly PrefetchPoint[],
  resolve: (point: PrefetchPoint) => { readonly key: string; readonly value: T },
  residentKeys: ReadonlySet<string>,
  loadingKeys: ReadonlySet<string>,
): { readonly key: string; readonly value: T } | undefined {
  const seen = new Set<string>()
  for (const point of points) {
    const candidate = resolve(point)
    if (seen.has(candidate.key)) continue
    seen.add(candidate.key)
    if (residentKeys.has(candidate.key) || loadingKeys.has(candidate.key)) continue
    return candidate
  }
  return undefined
}

/**
 * A later probe with no missing key must not erase work already admitted to
 * the single-flight queue. A newly ranked candidate may replace it, while
 * teleport cancellation remains the scheduler's responsibility.
 */
export function retainPendingPrefetch<T>(pending: T | undefined, candidate: T | undefined): T | undefined {
  return candidate ?? pending
}

export type PendingPrefetchAction = 'wait' | 'discard' | 'start'

/**
 * Guards the single queued request against teleport generations and load
 * ordering. A pending request never starts beside another load, and stale or
 * newly-resident work is discarded instead of being replayed later.
 */
export function pendingPrefetchAction(
  pendingGeneration: number,
  currentGeneration: number,
  pendingKey: string,
  residentKeys: ReadonlySet<string>,
  loadingCount: number,
): PendingPrefetchAction {
  if (pendingGeneration !== currentGeneration || residentKeys.has(pendingKey)) return 'discard'
  if (loadingCount > 0) return 'wait'
  return 'start'
}
